/**
 * Client for `/api/firm-knowledge/*`: the firm's library of exemplar
 * documents (uploads, batches, renderings, guidance suggestions) and its
 * skills (repeatable procedures the agents follow, plus their runs). The
 * caller's firm is implied by the token; platform admins can act for another
 * firm with `lawFirmId`. (The firm's standing instructions for its agents
 * live under `LawFirmsAPI.getAgentInstructions`.)
 */
import * as Sentry from "@sentry/react";

import { isAxiosError } from "axios";
import type {
  AutomationPreview,
  ConversionResponse,
  DeleteFolderResult,
  EntryEdits,
  EntryUsage,
  FirmLibraryBatch,
  FirmLibraryEntry,
  FirmLibraryFolder,
  FirmLibraryStatus,
  FirmLibraryVersion,
  FirmSkill,
  FirmSkillRun,
  FirmSkillStatus,
  FirmSkillSummary,
  FirmSkillVersion,
  LayoutProfile,
  LibraryListResponse,
  NewEntryFields,
  NewSkillFields,
  SkillEdits,
  SkillOptions,
  SkillRunsResponse,
  SkillTriggerEdit,
  SkillVersionsResponse,
  SuggestedReference,
  StartSkillRunResponse,
  SuggestionField,
  UpcomingAutomationItem,
  UploadPresets
} from "../features/firmKnowledge/types";

import axiosInstance from "./axiosConfig";
import { DraftConflictError } from "./draftErrors";
import { mapWithConcurrency, putToUploadSession } from "./utils/directUpload";
import { handleAPIError, handleBinaryAPIError } from "./utils/errors";

// axiosInstance's baseURL already ends in `/api`.
const BASE = "/firm-knowledge";

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export type FirmScope = { lawFirmId?: string | null };

const params = (scope?: FirmScope, extra?: Record<string, string>) => {
  const out: Record<string, string> = { ...(extra ?? {}) };
  if (scope?.lawFirmId) out.law_firm_id = scope.lawFirmId;
  return out;
};

const rethrow = (error: unknown): never => {
  Sentry.captureException(error);
  const apiError = handleAPIError(error);
  throw apiError;
};

const rethrowBinary = async (error: unknown): Promise<never> => {
  Sentry.captureException(error);
  const apiError = await handleBinaryAPIError(error);
  throw apiError;
};

const appendFields = (form: FormData, fields?: NewEntryFields) => {
  if (!fields) return;
  for (const [key, value] of Object.entries(fields)) {
    if (key === "tags") {
      for (const tag of (value as string[]) ?? []) form.append("tags", tag);
    } else if (value !== undefined && value !== null) {
      form.append(key, String(value));
    }
  }
};

// --- Library -----------------------------------------------------------------

export const listLibrary = async (
  scope?: FirmScope,
  status: FirmLibraryStatus | "all" = "all"
): Promise<LibraryListResponse> => {
  try {
    const response = await axiosInstance.get<LibraryListResponse>(
      `${BASE}/library`,
      { params: params(scope, { status }) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const getEntry = async (
  entryId: string,
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const response = await axiosInstance.get<FirmLibraryEntry>(
      `${BASE}/library/${entryId}`,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** The drafts started from an entry and the cases that read it, newest first. */
export const getEntryUsage = async (
  entryId: string,
  scope?: FirmScope
): Promise<EntryUsage> => {
  try {
    const response = await axiosInstance.get<EntryUsage>(
      `${BASE}/library/${entryId}/usage`,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const createEntry = async (
  file: File,
  fields?: NewEntryFields,
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  const form = new FormData();
  form.append("file", file, file.name);
  appendFields(form, fields);
  try {
    const response = await axiosInstance.post<FirmLibraryEntry>(
      `${BASE}/library`,
      form,
      {
        params: params(scope),
        headers: { "Content-Type": "multipart/form-data" }
      }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const updateEntry = async (
  entryId: string,
  edits: EntryEdits,
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const response = await axiosInstance.patch<FirmLibraryEntry>(
      `${BASE}/library/${entryId}`,
      edits,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const deleteEntry = async (
  entryId: string,
  scope?: FirmScope
): Promise<void> => {
  try {
    await axiosInstance.delete(`${BASE}/library/${entryId}`, {
      params: params(scope)
    });
  } catch (error) {
    rethrow(error);
  }
};

// --- Direct uploads ----------------------------------------------------------
//
// The bytes go straight to storage: the API opens a GCS resumable-upload
// session per file, the browser PUTs the file to it, and a batch or a
// replace-file names the upload by id. Cloud Run's 32 MiB request cap never
// applies.

export type UploadSession = {
  upload_id: string;
  /** GCS session URI; PUT the bytes here with `putToUploadSession`. */
  upload_url: string;
  /** The `Content-Type` the PUT must carry. */
  content_type: string;
};

/** Files uploaded to storage at once for one batch. */
const UPLOAD_CONCURRENCY = 4;

const newSessionId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID().replace(/-/g, "")
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

export const createUploadSession = async (
  sessionId: string,
  file: File,
  scope?: FirmScope
): Promise<UploadSession> => {
  const response = await axiosInstance.post<UploadSession>(
    `${BASE}/library/uploads`,
    {
      session_id: sessionId,
      filename: file.name,
      content_type: file.type || null,
      size_bytes: file.size
    },
    { params: params(scope) }
  );
  return response.data;
};

/** Open a session, PUT the file to it; resolves to the upload id. */
const uploadToStorage = async (
  sessionId: string,
  file: File,
  scope?: FirmScope,
  onLoaded?: (bytes: number) => void
): Promise<string> => {
  const session = await createUploadSession(sessionId, file, scope);
  await putToUploadSession(session.upload_url, file, {
    contentType: session.content_type,
    onProgress: (progress) => onLoaded?.(progress.loaded)
  });
  return session.upload_id;
};

export const replaceEntryFile = async (
  entryId: string,
  file: File,
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const sessionId = newSessionId();
    const uploadId = await uploadToStorage(sessionId, file, scope);
    const response = await axiosInstance.put<FirmLibraryEntry>(
      `${BASE}/library/${entryId}/file`,
      { session_id: sessionId, upload_id: uploadId },
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** The original file's bytes (for download). */
export const getEntryFile = async (
  entryId: string,
  scope?: FirmScope
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `${BASE}/library/${entryId}/file`,
      { params: params(scope, { download: "1" }), responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    return rethrowBinary(error);
  }
};

/** A bare `ETag` token (`W/"…"` or `"…"`); `null` when absent. */
const etagToken = (value: unknown): string | null => {
  if (typeof value !== "string" || !value.trim()) return null;
  return value.trim().replace(/^W\//, "").replace(/^"|"$/g, "") || null;
};

/**
 * A Word entry's bytes for the in-app editor, with the file's checksum: the
 * token `saveEntryDocx` sends back as `If-Match`.
 */
export const getEntryDocx = async (
  entryId: string,
  scope?: FirmScope
): Promise<{ bytes: ArrayBuffer; checksum: string | null }> => {
  try {
    const response = await axiosInstance.get<ArrayBuffer>(
      `${BASE}/library/${entryId}/file`,
      { params: params(scope), responseType: "arraybuffer" }
    );
    return {
      bytes: response.data,
      checksum: etagToken(response.headers?.etag)
    };
  } catch (error) {
    return rethrowBinary(error);
  }
};

/**
 * Saves the editor's docx over the entry's file. The entry keeps its fields
 * and its "converted from PDF" record; the renderings are rebuilt. A 409 (the
 * file changed since `baseChecksum` was loaded) becomes `DraftConflictError`
 * carrying the current checksum, so the editor's conflict notice takes over.
 */
export const saveEntryDocx = async (
  entryId: string,
  bytes: ArrayBuffer,
  baseChecksum: string | null,
  scope?: FirmScope
): Promise<{ entry: FirmLibraryEntry; checksum: string | null }> => {
  try {
    const response = await axiosInstance.put<FirmLibraryEntry>(
      `${BASE}/library/${entryId}/docx`,
      new Blob([bytes], { type: DOCX_MIME }),
      {
        params: params(scope),
        headers: {
          "Content-Type": DOCX_MIME,
          ...(baseChecksum ? { "If-Match": `"${baseChecksum}"` } : {})
        }
      }
    );
    return {
      entry: response.data,
      checksum: etagToken(response.headers?.etag)
    };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 409) {
      throw new DraftConflictError(
        etagToken(error.response.headers?.etag) ?? null
      );
    }
    return rethrow(error);
  }
};

/**
 * The entry as a PDF: the file itself, or the cached rendition of a docx.
 * 404 while the rendition is still pending.
 */
export const getEntryPdf = async (
  entryId: string,
  scope?: FirmScope
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `${BASE}/library/${entryId}/pdf`,
      { params: params(scope), responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    return rethrowBinary(error);
  }
};

/** The markdown rendering the agents read. */
export const getEntryRendering = async (
  entryId: string,
  scope?: FirmScope
): Promise<string> => {
  try {
    const response = await axiosInstance.get<string>(
      `${BASE}/library/${entryId}/rendering`,
      { params: params(scope), responseType: "text" }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const getEntryLayout = async (
  entryId: string,
  scope?: FirmScope
): Promise<LayoutProfile> => {
  try {
    const response = await axiosInstance.get<LayoutProfile>(
      `${BASE}/library/${entryId}/layout`,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const rerenderEntry = async (
  entryId: string,
  scope?: FirmScope
): Promise<void> => {
  try {
    await axiosInstance.post(`${BASE}/library/${entryId}/render`, null, {
      params: params(scope)
    });
  } catch (error) {
    rethrow(error);
  }
};

export const suggestForEntry = async (
  entryId: string,
  scope?: FirmScope
): Promise<void> => {
  try {
    await axiosInstance.post(`${BASE}/library/${entryId}/suggest`, null, {
      params: params(scope)
    });
  } catch (error) {
    rethrow(error);
  }
};

export const acceptSuggestion = async (
  entryId: string,
  options?: { fields?: SuggestionField[]; activate?: boolean },
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const response = await axiosInstance.post<FirmLibraryEntry>(
      `${BASE}/library/${entryId}/accept-suggestion`,
      { fields: options?.fields, activate: options?.activate ?? false },
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** Stop offering these suggested fields (a rename the firm does not want). */
export const dismissSuggestion = async (
  entryId: string,
  fields: SuggestionField[],
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const response = await axiosInstance.post<FirmLibraryEntry>(
      `${BASE}/library/${entryId}/dismiss-suggestion`,
      { fields },
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** Someone at the firm has looked at it: clears "Not reviewed yet". */
export const markEntryReviewed = async (
  entryId: string,
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const response = await axiosInstance.post<FirmLibraryEntry>(
      `${BASE}/library/${entryId}/reviewed`,
      null,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** A new entry beside the original with the client's details as `[[placeholders]]`. */
export const anonymizeEntry = async (
  entryId: string,
  scope?: FirmScope
): Promise<{ entry: FirmLibraryEntry; replaced: number }> => {
  try {
    const response = await axiosInstance.post<{
      entry: FirmLibraryEntry;
      replaced: number;
    }>(`${BASE}/library/${entryId}/anonymize`, null, {
      params: params(scope)
    });
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** Keep the client details found, as they are. */
export const keepClientDetails = async (
  entryId: string,
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const response = await axiosInstance.post<FirmLibraryEntry>(
      `${BASE}/library/${entryId}/client-details/clear`,
      null,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const listEntryVersions = async (
  entryId: string,
  scope?: FirmScope
): Promise<FirmLibraryVersion[]> => {
  try {
    const response = await axiosInstance.get<{
      versions: FirmLibraryVersion[];
    }>(`${BASE}/library/${entryId}/versions`, { params: params(scope) });
    return response.data.versions;
  } catch (error) {
    return rethrow(error);
  }
};

export const getEntryVersionFile = async (
  entryId: string,
  version: number,
  scope?: FirmScope
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `${BASE}/library/${entryId}/versions/${version}/file`,
      { params: params(scope), responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    return rethrowBinary(error);
  }
};

export const restoreEntryVersion = async (
  entryId: string,
  version: number,
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const response = await axiosInstance.post<FirmLibraryEntry>(
      `${BASE}/library/${entryId}/versions/${version}/restore`,
      null,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const getSuggestionTranscript = async (
  entryId: string,
  scope?: FirmScope
): Promise<string> => {
  try {
    const response = await axiosInstance.get<string>(
      `${BASE}/library/${entryId}/suggestion-transcript`,
      { params: params(scope), responseType: "text" }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

// --- Editable copy of a PDF (conversion) ---------------------------------------

/** Status and fidelity report of the copy. 404 when the entry has none. */
export const getEntryConversion = async (
  entryId: string,
  scope?: FirmScope,
  options?: { report?: boolean }
): Promise<ConversionResponse> => {
  try {
    const response = await axiosInstance.get<ConversionResponse>(
      `${BASE}/library/${entryId}/conversion`,
      {
        params: params(
          scope,
          options?.report === false ? { report: "0" } : undefined
        )
      }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** The reconstructed docx (for download). */
export const getConversionDocx = async (
  entryId: string,
  scope?: FirmScope
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `${BASE}/library/${entryId}/conversion/docx`,
      { params: params(scope), responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    return rethrowBinary(error);
  }
};

/** Pages of the copy, for the side-by-side review. 404 while pending. */
export const getConversionPdf = async (
  entryId: string,
  scope?: FirmScope
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `${BASE}/library/${entryId}/conversion/pdf`,
      { params: params(scope), responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    return rethrowBinary(error);
  }
};

/** The filed PDF: the entry's file, or the original kept after promotion. */
export const getConversionSourcePdf = async (
  entryId: string,
  scope?: FirmScope
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `${BASE}/library/${entryId}/conversion/source-pdf`,
      { params: params(scope), responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    return rethrowBinary(error);
  }
};

export const convertEntry = async (
  entryId: string,
  scope?: FirmScope
): Promise<void> => {
  try {
    await axiosInstance.post(`${BASE}/library/${entryId}/convert`, null, {
      params: params(scope)
    });
  } catch (error) {
    rethrow(error);
  }
};

/** The copy becomes the entry's file (a docx the agent copies and edits). */
export const promoteConversion = async (
  entryId: string,
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const response = await axiosInstance.post<FirmLibraryEntry>(
      `${BASE}/library/${entryId}/promote-conversion`,
      null,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** The filed PDF becomes the entry's file again. */
export const revertConversion = async (
  entryId: string,
  scope?: FirmScope
): Promise<FirmLibraryEntry> => {
  try {
    const response = await axiosInstance.post<FirmLibraryEntry>(
      `${BASE}/library/${entryId}/revert-conversion`,
      null,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

// --- Folders -----------------------------------------------------------------

export const listFolders = async (
  scope?: FirmScope
): Promise<FirmLibraryFolder[]> => {
  try {
    const response = await axiosInstance.get<{ folders: FirmLibraryFolder[] }>(
      `${BASE}/folders`,
      { params: params(scope) }
    );
    return response.data.folders ?? [];
  } catch (error) {
    return rethrow(error);
  }
};

export const createFolder = async (
  fields: { name: string; parent_id: string | null },
  scope?: FirmScope
): Promise<FirmLibraryFolder> => {
  try {
    const response = await axiosInstance.post<FirmLibraryFolder>(
      `${BASE}/folders`,
      fields,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** Rename (`name`) and/or move (`parent_id`, null for the top level). */
export const updateFolder = async (
  folderId: string,
  edits: { name?: string; parent_id?: string | null },
  scope?: FirmScope
): Promise<FirmLibraryFolder> => {
  try {
    const response = await axiosInstance.patch<FirmLibraryFolder>(
      `${BASE}/folders/${folderId}`,
      edits,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** Deletes the folder and its subfolders; their documents become Unfiled. */
export const deleteFolder = async (
  folderId: string,
  scope?: FirmScope
): Promise<DeleteFolderResult> => {
  try {
    const response = await axiosInstance.delete<DeleteFolderResult>(
      `${BASE}/folders/${folderId}`,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** Files several entries in one folder (null: Unfiled). */
export const moveEntries = async (
  entryIds: string[],
  folderId: string | null,
  scope?: FirmScope
): Promise<FirmLibraryEntry[]> => {
  try {
    const response = await axiosInstance.post<{ entries: FirmLibraryEntry[] }>(
      `${BASE}/library/move`,
      { entry_ids: entryIds, folder_id: folderId },
      { params: params(scope) }
    );
    return response.data.entries ?? [];
  } catch (error) {
    return rethrow(error);
  }
};

// --- Batches -----------------------------------------------------------------

export type CreateBatchResult = {
  batch: FirmLibraryBatch;
  /** Names of files that never reached storage; the batch went ahead without them. */
  failed: string[];
};

/**
 * Upload `files` straight to storage (4 at a time), then queue one batch
 * naming them. A file whose upload fails is left out and reported in
 * `failed`; if none arrives, this throws the first error.
 */
export const createBatch = async (
  files: File[],
  scope?: FirmScope,
  options?: {
    presets?: UploadPresets;
    /** 0..1 across every file's bytes. */
    onProgress?: (fraction: number) => void;
    /** 0..1 for one file, by its index in `files`. */
    onFileProgress?: (index: number, fraction: number) => void;
    /** Where every file goes; null or absent is Unfiled. */
    folderId?: string | null;
    /**
     * Each file's path in the drop (`"Harbor/Org Charts/chart.pdf"`), in the
     * order of `files`; the folders in it are created under `folderId`.
     */
    relativePaths?: string[];
    /** Off: every file (and every file in a zip) goes straight into `folderId`. */
    keepStructure?: boolean;
  }
): Promise<CreateBatchResult> => {
  const sessionId = newSessionId();
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0) || 1;
  const loaded = files.map(() => 0);
  const report = (index: number, bytes: number) => {
    loaded[index] = bytes;
    options?.onFileProgress?.(
      index,
      files[index].size ? Math.min(1, bytes / files[index].size) : 1
    );
    const sent = loaded.reduce((sum, n) => sum + n, 0);
    options?.onProgress?.(Math.min(1, sent / totalBytes));
  };

  const errors: unknown[] = [];
  const uploadIds = await mapWithConcurrency(
    files,
    UPLOAD_CONCURRENCY,
    async (file, index) => {
      try {
        const uploadId = await uploadToStorage(
          sessionId,
          file,
          scope,
          (bytes) => report(index, bytes)
        );
        report(index, file.size);
        return uploadId;
      } catch (error) {
        errors.push(error);
        return null;
      }
    }
  );
  if (uploadIds.every((id) => id === null)) return rethrow(errors[0]);
  errors.forEach((error) => Sentry.captureException(error));

  const paths =
    options?.relativePaths?.length === files.length
      ? options.relativePaths
      : null;
  const manifest = files.flatMap((file, index) => {
    const uploadId = uploadIds[index];
    if (uploadId === null) return [];
    return [
      {
        upload_id: uploadId,
        filename: file.name,
        ...(paths ? { relative_path: paths[index] } : {})
      }
    ];
  });
  const presets = options?.presets;
  try {
    const response = await axiosInstance.post<FirmLibraryBatch>(
      `${BASE}/library/batches`,
      {
        session_id: sessionId,
        files: manifest,
        folder_id: options?.folderId ?? null,
        keep_structure: options?.keepStructure !== false,
        category: presets?.category ?? null,
        visas: presets?.visas ?? [],
        tags: presets?.tags ?? []
      },
      { params: params(scope) }
    );
    return {
      batch: response.data,
      failed: files.filter((_, i) => uploadIds[i] === null).map((f) => f.name)
    };
  } catch (error) {
    return rethrow(error);
  }
};

// --- Skills ------------------------------------------------------------------

export const listSkills = async (
  scope?: FirmScope,
  status: FirmSkillStatus | "all" = "all"
): Promise<FirmSkill[]> => {
  try {
    const response = await axiosInstance.get<{ skills: FirmSkill[] }>(
      `${BASE}/skills`,
      { params: params(scope, { status }) }
    );
    return response.data.skills;
  } catch (error) {
    return rethrow(error);
  }
};

/** The active skills as the composer's picker sees them (id, slug, title, description). */
export const listActiveSkillSummaries = async (
  scope?: FirmScope
): Promise<FirmSkillSummary[]> => {
  try {
    const response = await axiosInstance.get<{ skills: FirmSkillSummary[] }>(
      `${BASE}/skills`,
      { params: params(scope, { status: "active", summary: "1" }) }
    );
    return response.data.skills;
  } catch (error) {
    return rethrow(error);
  }
};

export const getSkill = async (
  skillId: string,
  scope?: FirmScope
): Promise<FirmSkill> => {
  try {
    const response = await axiosInstance.get<FirmSkill>(
      `${BASE}/skills/${skillId}`,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const getSuggestedReferences = async (
  skillId: string,
  scope?: FirmScope
): Promise<SuggestedReference[]> => {
  try {
    const response = await axiosInstance.get<{
      suggestions: SuggestedReference[];
    }>(`${BASE}/skills/${skillId}/suggested-references`, {
      params: params(scope)
    });
    return response.data.suggestions;
  } catch (error) {
    return rethrow(error);
  }
};

export const createSkill = async (
  fields: NewSkillFields,
  scope?: FirmScope
): Promise<FirmSkill> => {
  try {
    const response = await axiosInstance.post<FirmSkill>(
      `${BASE}/skills`,
      fields,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const updateSkill = async (
  skillId: string,
  edits: SkillEdits,
  scope?: FirmScope
): Promise<FirmSkill> => {
  try {
    const response = await axiosInstance.patch<FirmSkill>(
      `${BASE}/skills/${skillId}`,
      edits,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const deleteSkill = async (
  skillId: string,
  scope?: FirmScope
): Promise<void> => {
  try {
    await axiosInstance.delete(`${BASE}/skills/${skillId}`, {
      params: params(scope)
    });
  } catch (error) {
    rethrow(error);
  }
};

/** Undo a delete; 409 when another skill has taken the name since. */
export const restoreSkill = async (
  skillId: string,
  scope?: FirmScope
): Promise<FirmSkill> => {
  try {
    const response = await axiosInstance.post<FirmSkill>(
      `${BASE}/skills/${skillId}/restore`,
      {},
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const setSkillStatus = async (
  skillId: string,
  status: "active" | "archived",
  scope?: FirmScope
): Promise<FirmSkill> => {
  try {
    const action = status === "active" ? "activate" : "archive";
    const response = await axiosInstance.post<FirmSkill>(
      `${BASE}/skills/${skillId}/${action}`,
      {},
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/**
 * "Run on this case": a case thread carrying the skill plus its run record.
 * With `threadId` the current thread adopts the skill instead of a new one.
 * `test` runs a draft too, always in Plan mode, and never counts as automated.
 */
export const startSkillRun = async (
  skillId: string,
  body: {
    individual_id: string;
    thread_id?: string;
    mode?: "agent" | "plan";
    inputs?: Record<string, string | string[]>;
    test?: boolean;
  },
  scope?: FirmScope
): Promise<StartSkillRunResponse> => {
  try {
    const response = await axiosInstance.post<StartSkillRunResponse>(
      `${BASE}/skills/${skillId}/runs`,
      body,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const listSkillRuns = async (
  skillId: string,
  scope?: FirmScope
): Promise<SkillRunsResponse> => {
  try {
    const response = await axiosInstance.get<SkillRunsResponse>(
      `${BASE}/skills/${skillId}/runs`,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** What an unsaved date automation would catch today; starts nothing. */
export const previewAutomation = async (
  skillId: string,
  trigger: SkillTriggerEdit,
  scope?: FirmScope
): Promise<AutomationPreview> => {
  try {
    const response = await axiosInstance.post<AutomationPreview>(
      `${BASE}/skills/${skillId}/automations/preview`,
      { trigger },
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** What the skill's date automations start in the next `days`, soonest first. */
export const listUpcomingAutomations = async (
  skillId: string,
  scope?: FirmScope,
  days = 30
): Promise<UpcomingAutomationItem[]> => {
  try {
    const response = await axiosInstance.get<{
      items: UpcomingAutomationItem[];
    }>(`${BASE}/skills/${skillId}/upcoming`, {
      params: { ...params(scope), days }
    });
    return response.data.items;
  } catch (error) {
    return rethrow(error);
  }
};

/** "Skip": the item never starts a run of this automation. */
export const skipUpcomingAutomation = async (
  skillId: string,
  triggerId: string,
  itemKey: string,
  scope?: FirmScope
): Promise<void> => {
  try {
    await axiosInstance.post(
      `${BASE}/skills/${skillId}/automations/${triggerId}/skip`,
      { item_key: itemKey },
      { params: params(scope) }
    );
  } catch (error) {
    rethrow(error);
  }
};

/** Automated runs on a case that wait on the attorney (the case header chip). */
export const listCaseAutomatedRuns = async (
  individualId: string,
  scope?: FirmScope
): Promise<FirmSkillRun[]> => {
  try {
    const response = await axiosInstance.get<{ runs: FirmSkillRun[] }>(
      `${BASE}/cases/${individualId}/automated-runs`,
      { params: params(scope) }
    );
    return response.data.runs;
  } catch (error) {
    return rethrow(error);
  }
};

/** Thumbs and a note on a run; an empty rating clears it. */
export const rateSkillRun = async (
  skillId: string,
  runId: string,
  body: { rating: "up" | "down" | ""; note?: string },
  scope?: FirmScope
): Promise<FirmSkillRun> => {
  try {
    const response = await axiosInstance.post<FirmSkillRun>(
      `${BASE}/skills/${skillId}/runs/${runId}/feedback`,
      body,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const listSkillVersions = async (
  skillId: string,
  scope?: FirmScope
): Promise<SkillVersionsResponse> => {
  try {
    const response = await axiosInstance.get<SkillVersionsResponse>(
      `${BASE}/skills/${skillId}/versions`,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const getSkillVersion = async (
  skillId: string,
  version: number,
  scope?: FirmScope
): Promise<FirmSkillVersion> => {
  try {
    const response = await axiosInstance.get<FirmSkillVersion>(
      `${BASE}/skills/${skillId}/versions/${version}`,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

/** Puts an earlier version's content back as a new version. */
export const revertSkillVersion = async (
  skillId: string,
  version: number,
  scope?: FirmScope
): Promise<FirmSkill> => {
  try {
    const response = await axiosInstance.post<FirmSkill>(
      `${BASE}/skills/${skillId}/versions/${version}/revert`,
      {},
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const getSkillOptions = async (
  scope?: FirmScope
): Promise<SkillOptions> => {
  try {
    const response = await axiosInstance.get<SkillOptions>(
      `${BASE}/skills/options`,
      { params: params(scope) }
    );
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

export const firmKnowledgeApi = {
  listSkills,
  listActiveSkillSummaries,
  getSkill,
  getSuggestedReferences,
  createSkill,
  updateSkill,
  deleteSkill,
  restoreSkill,
  setSkillStatus,
  startSkillRun,
  listSkillRuns,
  previewAutomation,
  listUpcomingAutomations,
  skipUpcomingAutomation,
  listCaseAutomatedRuns,
  rateSkillRun,
  listSkillVersions,
  getSkillVersion,
  revertSkillVersion,
  getSkillOptions,
  listLibrary,
  getEntry,
  getEntryUsage,
  createEntry,
  updateEntry,
  deleteEntry,
  replaceEntryFile,
  getEntryFile,
  getEntryDocx,
  saveEntryDocx,
  getEntryPdf,
  getEntryRendering,
  getEntryLayout,
  rerenderEntry,
  suggestForEntry,
  acceptSuggestion,
  dismissSuggestion,
  markEntryReviewed,
  anonymizeEntry,
  keepClientDetails,
  listEntryVersions,
  getEntryVersionFile,
  restoreEntryVersion,
  getSuggestionTranscript,
  getEntryConversion,
  getConversionDocx,
  getConversionPdf,
  getConversionSourcePdf,
  convertEntry,
  promoteConversion,
  revertConversion,
  createBatch,
  createUploadSession,
  listFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  moveEntries
};
